import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useUserStore from "../store/userStore";
import useCreateUserMedal from "../hooks/useCreateUserMedal";
import UserMedal from "../interfaces/userMedal";
import Medal from "../interfaces/medal";
import { ASSETS_URL } from "../util/constants";

const AdminEditMedals = ({ medals, userId }: { medals: UserMedal[] | null, userId: number | undefined}) => {
  const userToken = useUserStore((state) => state.token);

  const { mutate, error, isPending, isSuccess } = useCreateUserMedal();

  const findMedalIndex = (medalId: number) => {
    return medalsArray?.findIndex((m) => m.medal.id === medalId);
  };
  
  const schema = z.object({
    medals: z.array(
      z.object({
        quantity: z
          .number({ required_error: "Quantity is required" })
          .min(0, "Quantity cannot be negative")
          .default(0),
        medal: z.object({
          id: z.number(),
        }),
      })
    ),
  });

  const medalsArray = medals?.filter((medal) => medal.medal.medalType.slug === "collectionables");
  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      medals: medalsArray || [],
    },
  });

  const submit = async (data: FormData) => {
    console.log(data.medals);
    const medalsToMutate: UserMedal[] = data.medals.map(item => ({
            quantity: item.quantity,
            createdAt: new Date(),
            medal: item.medal as Medal
          }));

    mutate({token: userToken, userId: userId!.toString(), medals: medalsToMutate});
  };

  return (
    <form onSubmit={handleSubmit(submit, (errors) => console.log("Validation Errors:", errors))}>
      {error && <div className="bg-danger">Error while editing medals.</div>}
      {isSuccess && <div className="bg-success">Medals edited successfully.</div>}
      {isPending && <div className="bg-info">Saving medals...</div>}
      <h1>Edit medals</h1>
      <div
        className="mb-4 d-flex flex-wrap justify-content-around align-items-end"
        style={{ gap: "1.5rem" }}
      >
        {medalsArray!
          .map((medal) => {
            const index = findMedalIndex(medal.medal.id!);
            if (index === -1) return null;
            if (index === undefined) return null;
            return (
              <div key={medal.medal.id} className="text-center">
                <div>{medal.medal.name}</div>
                <img
                  src={`${ASSETS_URL}${medal.medal.pathImage}`}
                  alt={medal.medal.name}
                  style={{
                    width: "4rem",
                    height: "4rem",
                    objectFit: "contain",
                  }}
                  className="mb-2"
                />
                <input
                  type="number"
                  {...register(`medals.${index}.quantity`, {
                    valueAsNumber: true,
                  })}
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  className={`form-control form-control-sm ${
                    errors.medals?.[index]?.quantity ? "is-invalid" : ""
                  }`}
                />

                <input
                  type="hidden"
                  {...register(`medals.${index}.medal.id`, {
                    valueAsNumber: true,
                  })}
                />

                {errors.medals?.[index]?.quantity && (
                  <p
                    className="text-danger mt-1 mb-0"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {errors.medals[index]?.quantity?.message}
                  </p>
                )}
              </div>
            );
          })}
      </div>
      <button
        type="submit"
        className="btn btn-primary"
        style={{
          backgroundColor: "#5cb85c",
          borderColor: "#5cb85c",
          color: "#1d1f1c",
        }}
      >
        Save Medals
      </button>
    </form>
  );
};

export default AdminEditMedals;
